import React, { useEffect, useState, useCallback } from 'react';
import { Tree, Input, Divider } from 'antd';
import { InputProps } from 'antd/lib/input/Input';
import { TreeProps } from 'antd/lib/tree/Tree';

import style from './index.less';

const { TreeNode } = Tree;

const inputStyle = {
  maxWidth: 300,
  width: '100%'
};
const onStopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

type InputValue = string | number;

type EditInputProps = {
  editing: boolean;
  value: InputValue;
  label: string;
  onInputChange: (value: string) => void;
  inputProps: InputProps;
};

const EditInput = (props: EditInputProps) => {
  const { editing, value, label, onInputChange, inputProps } = props;
  if (editing) {
    return (
      <Input
        value={value}
        style={inputStyle}
        onClick={e => onStopPropagation(e)}
        size="small"
        onChange={e => onInputChange(e.target.value)}
        {...inputProps}
      />
    );
  }
  return <>{label}</>;
};

type OperateProps = {
  editing: boolean;
  inputValue: InputValue;
  toggleEdit: () => void;
  cancelEdit: () => void;
  setEditHidden?: () => void;
  handleOk: () => void;
  handleDelete: () => void;
  renderOperate?: (param: OperateProps) => JSX.Element;
  data?: TreeItemData;
};

const emptyFun = () => null;

const Operate = (props: OperateProps) => {
  const { editing, inputValue, toggleEdit, cancelEdit, setEditHidden, handleOk, handleDelete, data } = props;
  if (data && data.renderOperate) {
    return data.renderOperate({
      editing,
      toggleEdit,
      cancelEdit: setEditHidden || emptyFun,
      handleOk,
      handleDelete,
      data,
      inputValue
    });
  }
  if (editing) {
    return (
      <>
        <a onClick={cancelEdit}>取消</a>
        <Divider type="vertical" />
        <a onClick={handleOk}>确定</a>
      </>
    );
  }
  return (
    <>
      <a onClick={toggleEdit}>编辑</a>
      <Divider type="vertical" />
      <a onClick={handleDelete}>删除</a>
    </>
  );
};

type TreeItemData = {
  label: string;
  value: number | string;
  key: number | string;
  disableOperate: boolean;
  editing: boolean;
  disabled?: boolean;
  children?: Array<TreeItemData>;
  renderOperate?: (param: OperateProps) => JSX.Element;
};

type CallBackParams = {
  item: TreeItemData;
  value: InputValue;
  key: InputValue;
};

type EditTreeNodeTitleProps = {
  data: TreeItemData;
  onEdit: (key: InputValue) => void;
  onCancelEdit: (key: InputValue) => void;
  save?: (param: CallBackParams) => void;
  remove?: (param: CallBackParams) => void;
  onInputChange?: (param: CallBackParams) => void;
};

const EditTreeNodeTitle = ({
  data,
  onEdit,
  onCancelEdit,
  save,
  remove,
  onInputChange
}: EditTreeNodeTitleProps): JSX.Element => {
  const { label, value, key, disableOperate, editing } = data;
  const [inputValue, setInputValue] = useState<InputValue>('');
  const inputProps = {
    defaultValue: value
  };

  const toggleEdit = useCallback(() => {
    onEdit(key);
  }, [key]);

  const cancelEdit = () => {
    onCancelEdit(key);
    setInputValue(value);
  };

  const setEditHidden = () => onCancelEdit(key);

  const handleOk = () => {
    if (save) {
      setEditHidden();
      if (value !== inputValue) {
        save({ item: data, value: inputValue, key });
      }
    }
  };

  const handleDelete = () => {
    if (remove) {
      remove({ item: data, value, key });
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (onInputChange) {
      onInputChange({ item: data, value, key });
    }
  };

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  if (!disableOperate) {
    return (
      <div className={style['tree-title-box']}>
        <div className={style['tree-title-box-left']}>
          <EditInput
            editing={editing}
            inputProps={inputProps}
            value={inputValue}
            onInputChange={handleInputChange}
            label={label}
          />
        </div>
        <div onClick={onStopPropagation} className="hover-visible">
          <Operate
            inputValue={inputValue}
            editing={editing}
            toggleEdit={toggleEdit}
            cancelEdit={cancelEdit}
            setEditHidden={setEditHidden}
            handleOk={handleOk}
            handleDelete={handleDelete}
            data={data}
          />
        </div>
      </div>
    );
  }
  return <>{label}</>;
};

type EditTreeProps = {
  data: Array<TreeItemData>;
  treeProps: TreeProps;
  children: Array<TreeItemData>;
  className: string;
  onTreeRender?: (data: any) => void;
};

const numberToString = (key: string | number): string => `${key}`;

const EditTree = (props: EditTreeProps): JSX.Element => {
  const [editingList, setEditingList] = useState<Array<InputValue>>([]);
  const { data = [], treeProps, children, className, onTreeRender } = props;
  const hasChildren = children && children.length;

  useEffect(() => {
    onTreeRender && onTreeRender(data);
  }, []);

  const onEdit = (key: InputValue) => {
    if (!editingList.includes(key)) {
      const newEditingList = [...editingList, key];
      setEditingList(newEditingList);
    }
  };

  const onCancelEdit = (key: InputValue) => {
    if (editingList.includes(key)) {
      setEditingList(editingList.filter(val => val !== key));
    }
  };

  const renderTreeNodes = (data: Array<TreeItemData>) =>
    data.map(item => {
      const { children, key, disabled } = item;
      const extraProps = {
        onEdit,
        onCancelEdit
      };

      let editing = editingList.includes(key);
      // 判断组件是否受控
      if (item.editing !== undefined) {
        editing = item.editing;
      }

      if (item.children && item.children.length > 0) {
        return (
          <TreeNode
            disabled={disabled}
            title={<EditTreeNodeTitle {...extraProps} data={{ ...item, editing }} />}
            key={numberToString(key)}
            dataRef={item}>
            {renderTreeNodes(children || [])}
          </TreeNode>
        );
      }
      return (
        <TreeNode
          disabled={disabled}
          title={<EditTreeNodeTitle {...extraProps} data={{ ...item, editing }} />}
          key={numberToString(key)}
          dataRef={item}
        />
      );
    });

  return (
    <Tree className={`${style['edit-tree']} ${className}`} {...treeProps} blockNode={true}>
      {hasChildren ? children : renderTreeNodes(data)}
    </Tree>
  );
};

EditTree.EditTreeNodeTitle = EditTreeNodeTitle;

export default EditTree;
